import { z } from 'zod';

/**
 * World Manifest schema v3.2.
 *
 * Source: 03-RESEARCH.md lines 866-897 (base shape) + 04-RESEARCH.md Pattern 5
 * (VistaZoneSchema) + 04-RESEARCH.md Open Question #4 (aboutPageSourceIds) +
 * 05-RESEARCH.md Patterns 1/3/4/5 (streetcarSpline / tours / audioSources / minimapPois).
 *
 * v3.2 (Phase 5 Plan 05-01) additions over v3.1:
 *   - `streetcarSpline?: StreetcarSpline` — waypoints + durationS for the
 *      NAV-02 scripted ride through Lone Pine Gap to the lakeshore
 *   - `tours: Tour[]` — guided-tour spline authoring (NAV-05); defaults to []
 *   - `audioSources: AudioSource[]` — positional-audio authoring (AUDIO-01/02); defaults to []
 *   - `minimapPois: MinimapPoi[]` — top-down minimap POIs (UI-03); defaults to []
 *
 * Version is a clean break — literal bump from '3.1' → '3.2' (no dual-accept;
 * matches Phase 4 Plan 04-01 convention). Optional array fields default to []
 * so hand-authored v3.2 files can omit them entirely. `streetcarSpline` is
 * optional (no default) because there is no sensible default shape.
 */
export const ManifestObjectSchema = z.object({
  id: z.string(),
  model: z.string().optional(), // Phase 3 allows inline-geometry placeholders without a .glb
  era: z.enum(['1925', '2026', 'shared']),
  position: z.tuple([z.number(), z.number(), z.number()]),
  rotation: z.tuple([z.number(), z.number(), z.number()]).default([0, 0, 0]),
  scale: z.number().default(1),
  confidence: z.enum(['documented', 'inferred', 'stand-in']).default('stand-in'),
  sourceIds: z.array(z.string()).default([]),
  placeholder: z
    .object({
      geometry: z.enum(['box', 'plane', 'cylinder']),
      size: z.tuple([z.number(), z.number(), z.number()]),
      color: z.string().default('#B8602C'),
      collision: z.boolean().default(false),
    })
    .optional(),
});

/**
 * VistaZone — a hero vantage where the era toggle (ERA-01) is active.
 *
 * Phase 4 ships 3 seeded stubs (dam, pavilion, streetcar stop); Phase 7's
 * era-toggle UI renders the button only when the camera is inside one of
 * these zones (sphere around `position` with given `radius`). On activation,
 * the `eraPhoto` (period-matched present-day photograph) fades in as a
 * billboard plane pinned to the camera frustum. `photoRotation` optionally
 * aligns the photo orientation to the camera yaw for the zone.
 */
export const VistaZoneSchema = z.object({
  id: z.string(),
  label: z.string(),
  position: z.tuple([z.number(), z.number(), z.number()]),
  radius: z.number().default(3),
  eraPhoto: z.string(),
  photoRotation: z.tuple([z.number(), z.number(), z.number()]).default([0, 0, 0]),
});

/**
 * StreetcarSpline — waypoints + duration for the NAV-02 scripted ride.
 *
 * Plan 05-01 Task 1 contract. StreetcarRig (Task 2) builds a
 * THREE.CatmullRomCurve3 from `waypoints` (centripetal, tension 0.5, open),
 * rides it via `curve.getPointAt(t)` (arc-length-uniform — per 05-RESEARCH.md
 * Pitfall 1), and uses a look-ahead-point for `camera.lookAt` (Pitfall 2).
 *
 * `waypoints.length >= 2` is enforced — a single point is not a spline.
 * `durationS` drives the ride pacing (default fallback is 90s in
 * streetcarSpline.ts if omitted here).
 */
export const StreetcarSplineSchema = z.object({
  waypoints: z
    .array(z.tuple([z.number(), z.number(), z.number()]))
    .min(2, 'streetcarSpline.waypoints requires at least 2 points'),
  durationS: z.number().positive(),
});

/**
 * TourStep — caption shown when the tour rig's normalized progress crosses
 * `triggerAtT`. Captions render in a DOM CaptionOverlay (Plan 05-03).
 */
export const TourStepSchema = z.object({
  caption: z.string(),
  triggerAtT: z.number().min(0).max(1),
});

/**
 * Tour — a named guided-tour spline authored in the manifest (NAV-05).
 *
 * TourRig (Plan 05-03) follows the same CatmullRomCurve3 pattern as
 * StreetcarRig, at a slower `durationS`, with caption events firing at each
 * step's `triggerAtT`. waypoints.length >= 2 is enforced (single-point tours
 * are degenerate).
 */
export const TourSchema = z.object({
  id: z.string(),
  name: z.string(),
  waypoints: z
    .array(z.tuple([z.number(), z.number(), z.number()]))
    .min(2, 'tour.waypoints requires at least 2 points'),
  durationS: z.number().positive(),
  steps: z.array(TourStepSchema).default([]),
});

/**
 * AudioSource — positional audio emitter (AUDIO-01 / AUDIO-02).
 *
 * Plan 05-05 wires each entry to a drei <PositionalAudio> with
 * setRefDistance / setRolloffFactor / setMaxDistance / setDistanceModel
 * so proximity-based intensity is native, not custom math.
 *
 * Invariants:
 *   - refDistance > 0
 *   - rolloffFactor > 0
 *   - maxDistance > refDistance (so the rolloff envelope is meaningful)
 */
export const AudioSourceSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    position: z.tuple([z.number(), z.number(), z.number()]),
    refDistance: z.number().positive(),
    rolloffFactor: z.number().positive(),
    maxDistance: z.number().positive(),
    loop: z.boolean(),
  })
  .refine((s) => s.maxDistance > s.refDistance, {
    message: 'audioSource.maxDistance must be greater than refDistance',
    path: ['maxDistance'],
  });

/**
 * SpawnPose — walk-ready camera pose at the lakeshore (NAV-03).
 *
 * Plan 05-02 Task 1 contract. ArrivalCinematic damps the camera from its
 * spline-end pose (written by StreetcarRig) to this target over ~2 seconds
 * using THREE.Vector3.lerp + THREE.Quaternion.slerp + easeInOutQuad.
 *
 * Defaults keep existing v3.2 manifests (Plan 05-01's world.json) valid
 * without edits — if the author omits spawnPose, the cinematic targets the
 * coordinate origin at eye height with identity rotation. This is an
 * ADDITIVE change, not a version bump.
 *
 * Authored in world.json as:
 *   "spawnPose": {
 *     "position": [40, 2, -260],      // [x, y, z] world-space
 *     "quaternion": [0, 1, 0, 0]      // [x, y, z, w] — 180° Y rotation
 *   }
 */
export const SpawnPoseSchema = z
  .object({
    position: z
      .tuple([z.number(), z.number(), z.number()])
      .default([0, 1.7, 0]),
    quaternion: z
      .tuple([z.number(), z.number(), z.number(), z.number()])
      .default([0, 0, 0, 1]),
  })
  .default({ position: [0, 1.7, 0], quaternion: [0, 0, 0, 1] });

/**
 * MinimapPoi — top-down minimap point of interest (UI-03).
 *
 * Plan 05-04 renders each entry as an SVG marker on the 2D minimap, colored
 * by category. Categories are a closed enum so the UI can map each to a
 * stable color without string-matching.
 */
export const MinimapPoiSchema = z.object({
  id: z.string(),
  label: z.string(),
  position: z.tuple([z.number(), z.number(), z.number()]),
  category: z.enum(['pavilion', 'dam', 'streetcar-stop', 'landmark']),
});

export const WorldManifestSchema = z.object({
  version: z.literal('3.2'),
  coordinateSystem: z.literal('local-stylized'),
  origin: z.object({
    name: z.string(),
    lat: z.number().optional(),
    lon: z.number().optional(),
  }),
  objects: z.array(ManifestObjectSchema),
  outerBoundary: z.object({
    min: z.tuple([z.number(), z.number(), z.number()]),
    max: z.tuple([z.number(), z.number(), z.number()]),
  }),
  vistaZones: z.array(VistaZoneSchema).default([]),
  aboutPageSourceIds: z.array(z.string()).default([]),
  streetcarSpline: StreetcarSplineSchema.optional(),
  spawnPose: SpawnPoseSchema,
  tours: z.array(TourSchema).default([]),
  audioSources: z.array(AudioSourceSchema).default([]),
  minimapPois: z.array(MinimapPoiSchema).default([]),
});

export type WorldManifest = z.infer<typeof WorldManifestSchema>;
export type ManifestObject = z.infer<typeof ManifestObjectSchema>;
export type VistaZone = z.infer<typeof VistaZoneSchema>;
export type StreetcarSpline = z.infer<typeof StreetcarSplineSchema>;
export type Tour = z.infer<typeof TourSchema>;
export type TourStep = z.infer<typeof TourStepSchema>;
export type AudioSource = z.infer<typeof AudioSourceSchema>;
export type MinimapPoi = z.infer<typeof MinimapPoiSchema>;
export type SpawnPose = z.infer<typeof SpawnPoseSchema>;
